/**
 * The car catalogue: makes, their models, and the colours a car can be.
 *
 * Reference data, not a resource this client owns. Nothing on the web mutates
 * it (the admin application does), so these are deliberately untagged: there is
 * no mutation here for a tag to invalidate, and inventing one would only
 * suggest otherwise.
 *
 * A car stores its make, model and colour as free text, not as ids. These lists
 * exist to stop three drivers typing "Toyota", "toyota" and "Toyata", not to
 * constrain what the column can hold. Two consequences the callers have to
 * respect: a saved value has to render as chosen even when the fetched list
 * does not contain it, and a catalogue that will not load must not stop someone
 * registering a vehicle. `CatalogueSelect` handles both.
 *
 * The API sends `cache-control: public, max-age=300`. `keepUnusedDataFor`
 * matches it, so moving between the vehicle screens inside five minutes costs
 * no request at all.
 */

import { api } from "../api";
import type { CarColour, CarMake, CarModel, CarModelsArgs } from "../types";

/** The API caches these for five minutes; there is no point holding them less. */
const FIVE_MINUTES = 300;

export const catalogueApi = api.injectEndpoints({
    endpoints: (build) => ({
        /** Every make, name-sorted, each with the number of models it has. */
        carMakes: build.query<CarMake[], void>({
            query: () => ({ url: "catalogue/makes" }),
            keepUnusedDataFor: FIVE_MINUTES,
        }),

        /**
         * The models of one make, optionally filtered by a substring.
         *
         * A malformed make id is a 422 and an unknown one a 404, so a caller
         * that has only a free-text make name must not guess an id: it has no
         * model list to offer and should fall back to free text.
         */
        carModels: build.query<CarModel[], CarModelsArgs>({
            query: ({ makeId, query }) => ({
                url: `catalogue/makes/${makeId}/models`,
                ...(query ? { params: { query } } : {}),
            }),
            keepUnusedDataFor: FIVE_MINUTES,
        }),

        /** Every colour, with a hex value for a swatch. */
        carColours: build.query<CarColour[], void>({
            query: () => ({ url: "catalogue/colours" }),
            keepUnusedDataFor: FIVE_MINUTES,
        }),
    }),
});

export const {
    useCarMakesQuery,
    useCarModelsQuery,
    useCarColoursQuery,
} = catalogueApi;
